import React, { useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Lightbulb } from "lucide-react";
import { fetchItems, fetchItemDescription } from "../../../../store/itemSlice";
import { IndentPDF } from "../../../../components/Header/IndentPDF";
import PrinterButton from "../../IndentComponents/PrinterButton";
import { difference } from "lodash";

const LoadingSkeleton = () => (
  <div className="animate-pulse">
    <div className="flex justify-between items-center mb-4">
      <div className="h-8 w-32 bg-gray-200 rounded"></div>
      <div className="h-8 w-8 bg-gray-200 rounded-full"></div>
    </div>
    <div className="space-y-4">
      {[1, 2, 3].map((i) => (
        <div key={i} className="grid grid-cols-8 gap-4">
          <div className="h-8 bg-gray-200 rounded col-span-1"></div>
          <div className="h-8 bg-gray-200 rounded col-span-2"></div>
          <div className="h-8 bg-gray-200 rounded col-span-1"></div>
          <div className="h-8 bg-gray-200 rounded col-span-1"></div>
          <div className="h-8 bg-gray-200 rounded col-span-1"></div>
          <div className="h-8 bg-gray-200 rounded col-span-1"></div>
          <div className="h-8 bg-gray-200 rounded col-span-1"></div>
        </div>
      ))}
    </div>
  </div>
);

const DescriptionCell = React.memo(({ itemId }) => {
  const description = useSelector((state) => state.items.descriptions[itemId]);

  return (
    <div className="relative group">
      <Lightbulb
        size={18}
        className={`transition-colors duration-200 ${
          description ? "text-yellow-500" : "text-gray-400"
        }`}
      />
      {description && (
        <div className="invisible group-hover:visible absolute z-10 w-64 p-2 mt-2 text-sm text-white bg-gray-900 rounded-lg shadow-lg left-1/2 transform -translate-x-1/2">
          {description}
        </div>
      )}
    </div>
  );
});

const ItemsTable = React.memo(
  ({
    indentID,
    isDraft,
    onPrint,
    indentStatus = { l1: 0, l2: 0, po: 0, ph: 0, so: 0 },
    mobileView = false,
    hideHeader = false,
  }) => {
    const dispatch = useDispatch();
    const items = useSelector((state) => state.items.items);
    const status = useSelector((state) => state.items.status);
    const error = useSelector((state) => state.items.error);
    const [pdfError, setPdfError] = useState("");
    const [typeName, setTypeName] = useState("");

    const tableHeaders = useMemo(
      () => (
        <tr>
          <th className="px-4 py-3 text-left w-12">S.No</th>
          <th className="px-4 py-3 text-left">Item Name</th>
          <th className="px-4 py-3 text-center w-8"></th>
          <th className="px-4 py-3 text-left">AU</th>
          <th className="px-4 py-3 text-center w-24">Quantity</th>
          <th className="px-4 py-3 text-right">Stock</th>
          <th className="px-4 py-3 text-right">Committed Date</th>
          <th className="px-4 py-3 text-right">Promised Days</th>
          {indentStatus.so === 1 && (
            <>
              <th className="px-4 py-3 text-right">Received Qty</th>
              <th className="px-4 py-3 text-right">
                {indentStatus.ph === 1 ? "SH Remarks" : "Difference"}
              </th>
            </>
          )}
        </tr>
      ),
      [indentStatus.so, indentStatus.ph]
    );

    const fetchIndentData = async () => {
      const indentResponse = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/indent/indents/${indentID}`
      );
      const indentData = await indentResponse.json();
      const indentDetails = Array.isArray(indentData)
        ? indentData[0]
        : indentData;

      // Fetch items with category information
      const itemsResponse = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/indent/indentitems/${indentID}`
      );
      const itemsData = await itemsResponse.json();

      // Fetch indent status
      const statusResponse = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/indent/status/${indentID}`
      );
      const statusData = await statusResponse.json();
      const status = Array.isArray(statusData) ? statusData[0] : statusData;

      // Format dates consistently
      const formatDate = (dateString) => {
        if (!dateString) return null;
        try {
          const date = new Date(dateString);
          return date.toLocaleDateString('en-US', { 
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          });
        } catch (error) {
          console.error('Error formatting date:', error);
          return dateString;
        }
      };

      return {
        indentId: indentID,
        creationDate: formatDate(indentDetails?.CreationDate),
        PEApprovalDate: formatDate(indentDetails?.PEApprovalDate || indentDetails?.PEApprovedDate),
        L1ApprovedDate: formatDate(indentDetails?.L1ApprovedDate),
        L2ApprovalDate: formatDate(indentDetails?.L2ApprovalDate),
        projectName: indentDetails?.ProjectName || "N/A",
        userName: indentDetails?.UserName || "N/A",
        status: indentDetails?.Status || "N/A",
        taskScheduled: indentDetails?.TaskSchedule || "N/A",
        invoicingType: indentDetails?.TypeName || "N/A",
        // Add approval flags
        PO: status?.PO || indentDetails?.PO || 0,
        L1: status?.L1 || indentDetails?.L1 || 0,
        L2: status?.L2 || indentDetails?.L2 || 0,
        items: itemsData.map((item) => ({
          name: item.ItemName,
          category: item.CategoryName,
          au: item.UnitName,
          quantity: item.Quantity,
          stock: item.Stock,
          description: item.Description || "__",
          promisedDays: item.NormalDays,
          promisedDays2: item.EmergencyDays,
          committedDate: item.CommitedDate,
          promisedDate: item.PromisedDate,
        })),
      };
    };

    const getPDFComponent = (data) => {
      return <IndentPDF data={data} />;
    };

    useEffect(() => {
      const fetchIndentDetails = async () => {
        try {
          const response = await fetch(
            `${process.env.REACT_APP_API_BASE_URL}/l2page/indents/${indentID}`
          );
          const data = await response.json();
          const indentDetails = Array.isArray(data) ? data[0] : data;
          setTypeName(indentDetails?.TypeName || "");
        } catch (error) {
          console.error("Error fetching indent details:", error);
        }
      };

      if (indentID) {
        fetchIndentDetails();
        dispatch(fetchItems(indentID)).then((action) => {
          const items = action.payload;
          items.forEach((item) => {
            if (!item.description) {
              dispatch(fetchItemDescription({ itemId: item.itemID, indentID }));
            }
          });
        });
      }
    }, [dispatch, indentID]);

    const MobileItemCard = ({ item, index }) => (
      <div className="bg-white p-4 rounded-lg shadow-sm mb-4 border border-gray-200">
        {/* Header Section */}
        <div className="flex justify-between items-center mb-4 pb-2 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-gray-700">Item #{index + 1}</span>
          </div>
          <DescriptionCell itemId={item.itemID} indentID={indentID} />
        </div>

        {/* Main Content */}
        <div className="space-y-4">
          {/* Item Name */}
          <div className="flex flex-col gap-1">
            <span className="text-xs font-medium text-gray-500">Item Name</span>
            <span className="text-sm font-semibold text-gray-800">{item.itemName}</span>
          </div>

          {/* Details Grid */}
          <div className="grid grid-cols-2 gap-4">
            {/* Left Column */}
            <div className="space-y-3">
              <div className="flex flex-col gap-1">
                <span className="text-xs font-medium text-gray-500">AU</span>
                <span className="text-sm text-gray-800">{item.unitname}</span>
              </div>

              <div className="flex flex-col gap-1">
                <span className="text-xs font-medium text-gray-500">Quantity</span>
                <span className="text-sm text-gray-800">{item.quantity}</span>
              </div>

              <div className="flex flex-col gap-1">
                <span className="text-xs font-medium text-gray-500">Committed Date</span>
                <span className="text-sm text-gray-800">
                  {new Date(item.committedDate).toLocaleDateString()}
                </span>
              </div>
            </div>

            {/* Right Column */}
            <div className="space-y-3">
              <div className="flex flex-col gap-1">
                <span className="text-xs font-medium text-gray-500">Stock</span>
                <span className="text-sm text-gray-800">{item.stock}</span>
              </div>

              <div className="flex flex-col gap-1">
                <span className="text-xs font-medium text-gray-500">Promised Days</span>
                <span className="text-sm text-gray-800">
                  {typeName?.toLowerCase() === "normal" ? item.normalDays : item.emergencyDays}
                </span>
              </div>
            </div>
          </div>

          {/* Additional Info (if SO is 1) */}
          {indentStatus.so === 1 && (
            <div className="grid grid-cols-2 gap-4 pt-2 border-t border-gray-100">
              <div className="flex flex-col gap-1">
                <span className="text-xs font-medium text-gray-500">Received Qty</span>
                <span className="text-sm text-gray-800">{item.quantityReceived || 0}</span>
              </div>

              <div className="flex flex-col gap-1">
                <span className="text-xs font-medium text-gray-500">
                  {indentStatus.ph === 1 ? "SH Remarks" : "Difference"}
                </span>
                <span className="text-sm text-gray-800">
                  {indentStatus.ph === 1
                    ? `FC ${item.difference}, ${item.shRemarks || ""}`
                    : item.difference || 0}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
    );

    if (status === "loading") {
      return (
        <div className="bg-white shadow-md rounded-lg p-6 border border-gray-200">
          <LoadingSkeleton />
        </div>
      );
    }

    return (
      <div className="bg-white shadow-md rounded-lg p-6 border border-gray-200">
        {(error || pdfError) && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error || pdfError}
          </div>
        )}
        {!hideHeader && (
        <div className="flex justify-between items-center border-b pb-4 mb-4">
          <h2 className="text-xl font-bold text-gray-800">Invoice Items</h2>
          <PrinterButton
            getPDFComponent={getPDFComponent}
            filename={`indent-${indentID}`}
            fetchData={fetchIndentData}
            onError={(error) =>
              setPdfError("Failed to generate PDF. Please try again.")
            }
          />
        </div>
        )}
        
        {mobileView ? (
          <div className="space-y-4">
            {items.length > 0 ? (
              items.map((item, index) => (
                <MobileItemCard key={item.indentItemID} item={item} index={index} />
              ))
            ) : (
              <div className="text-center text-gray-500 py-6">
                No items added to the invoice
              </div>
            )}
          </div>
        ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-gray-600">
            <thead className="bg-gray-100 text-gray-700 uppercase">
              {tableHeaders}
            </thead>
            <tbody>
              {items.length > 0 ? (
                items.map((item, index) => (
                  <tr
                    key={item.indentItemID}
                    className="border-b hover:bg-gray-50 transition-colors duration-200"
                  >
                    <td className="px-4 py-3 text-gray-500">{index + 1}</td>
                    <td className="px-4 py-3 font-medium">{item.itemName}</td>
                    <td className="px-4 py-3 text-center">
                      <DescriptionCell
                        itemId={item.itemID}
                        indentID={indentID}
                      />
                    </td>
                    <td className="px-4 py-3 font-medium">{item.unitname}</td>
                    <td className="px-4 py-3 text-center">{item.quantity}</td>
                    <td className="px-4 py-3 text-right">{item.stock}</td>
                    <td className="px-4 py-3 text-right">
                      {new Date(item.committedDate).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {typeName?.toLowerCase() === "normal"
                        ? item.normalDays
                        : item.emergencyDays}
                    </td>
                    {indentStatus.so === 1 && (
                      <>
                        <td className="px-4 py-3 text-right">
                          {item.quantityReceived || 0}
                        </td>
                        <td className="px-4 py-3 text-right">
                          {indentStatus.ph === 1
                            ? `FC ${item.difference}, ${item.shRemarks || ""}`
                            : item.difference || 0}
                        </td>
                      </>
                    )}
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={indentStatus.so === 1 ? 10 : 8}
                    className="text-center text-gray-500 py-6"
                  >
                    No items added to the invoice
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        )}
      </div>
    );
  }
);

export default ItemsTable;